import { useState } from "react";
import { Link } from "react-router-dom";

import { ChevronRight, X } from "lucide-react";

import { useAuth } from "@/hooks";
import { pilotAccess } from "@/lib/pilot-access";
import { APP_CONSTANTS } from "@/config";

const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;

function readDismissedAt(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(
      APP_CONSTANTS.UPGRADE_BANNER_LOCAL_STORAGE_KEY
    );
    if (!raw) return null;
    const at = new Date(raw).getTime();
    return Number.isNaN(at) ? null : at;
  } catch {
    return null;
  }
}

function persistDismissal() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      APP_CONSTANTS.UPGRADE_BANNER_LOCAL_STORAGE_KEY,
      new Date().toISOString()
    );
  } catch {
    // A blocked storage only costs the clinic the seven-day silence.
  }
}

function dismissedRecently(): boolean {
  const at = readDismissedAt();
  return at !== null && Date.now() - at < SEVEN_DAYS_IN_MS;
}

/**
 * Trial upsell, shown as a banner in the page flow — never as a modal and
 * never on a timer (owner decision, 2026-09-09). Dismissing it hides it for
 * seven days, and nothing re-opens it inside the session.
 *
 * SCRUM-208 (audit C-04): the banner used to fetch the legacy plan catalogue
 * and link straight to `plans[0].id` — the clinic never chose, the array
 * order did — and it rendered nothing at all when that call failed, so a
 * network error silently removed the trial warning. It is a fixed link to the
 * one purchase surface now, and it shows whenever the clinic is on trial.
 */
export function UpgradeBanner() {
  const { user: currentUser } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(dismissedRecently);

  if (!currentUser?.is_trial || (pilotAccess(currentUser).hasPilot || currentUser.pilot_read_only)) return null;
  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    persistDismissal();
  };

  return (
    <div
      role="region"
      aria-label="Aviso do período de teste"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-accent-warm bg-accent-warm-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-app-fg-strong">
        Você está no período de teste. Escolha um plano para continuar com todos
        os recursos da clínica.
      </p>
      <div className="flex items-center gap-1">
        <Link
          to="/onboarding/billing"
          className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-brand transition-colors duration-150 hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          Escolher plano
          <ChevronRight className="size-4" aria-hidden />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dispensar aviso do período de teste"
          className="flex size-10 items-center justify-center rounded-lg text-app-fg-muted transition-colors duration-150 hover:bg-accent-warm hover:text-app-fg-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
